import DropdownSelector from 'components/DropdownSelector';
import BaseSearchDropdown from 'features/components/BaseSearchDropdown';
import {
  selectLinkingGroupId,
  selectSelectedDeviceId,
} from 'features/Integrations/state/selectors/deviceSelectors';
import {
  selectHistoryDataLoading,
  selectInitialHistoryLoaded,
  selectIntegrationFetching,
} from 'features/Integrations/state/selectors/integrationSelectors';
import { setSelectedDeviceId } from 'features/Integrations/state/slices/deviceSlice';
import {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetConnectedDevicesQuery,
  useLazyGetNodeDataByTimestampsQuery,
} from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { safeJSONParse } from 'utils/data/parse.utils';
import ConnectedDevicesLoadingState from '../../components/ConnectedDevicesLoadingState/ConnectedDevicesLoadingState';
import DropdownSelect from './components/DropdownSelect/DropdownSelect';
import {
  getDefaultGraphMetric,
  getGraphDeviceIds,
  getGraphDeviceOptions,
  getGraphMetricOptions,
  getSelectedGraphFamily,
  isGraphOptionDisabled,
} from './Graph/GraphRegistry';
import GraphRenderer from './Graph/GraphRenderer/GraphRenderer';
import { useGraphBuildResult } from './hooks/useGraphBuildResult';
import {
  DEFAULT_WIDGET_TIME_INDEX,
  fetchGraphSeriesPages,
  getWidgetTimeRangeFromOption,
  MAX_GRAPH_POINTS_PER_DEVICE,
  resolveGroupDeviceMap,
  WIDGET_TIME_OPTIONS,
} from './utils/deviceGraph.utils';

const DeviceGraph = ({
  title,
  startTime,
  endTime,
  setShowCurrentDevice,
  onSelectedDeviceIndex,
  setShowProvisional,
  widget = false,
  expandButton = null,
  constrainControls = false,
  graphControlsAlignment = 'default',
}) => {
  const { data: connectedDevices = [], isLoading: connectedDevicesLoading } =
    useGetConnectedDevicesQuery();
  const [fetchNodeDataByTimestamps] = useLazyGetNodeDataByTimestampsQuery();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const graphDeviceIds = useMemo(() => getGraphDeviceIds(connectedDevices), [connectedDevices]);

  const [unitType, setUnitType] = useState(false);

  const [selectedDeviceMap, setSelectedDeviceMap] = useState(
    safeJSONParse(
      sessionStorage.getItem('selectedDeviceMap'),
      graphDeviceIds[0] ? { [String(graphDeviceIds[0])]: true } : {},
    ),
  );

  const [selectedDataType, setSelectedDataType] = useState(
    safeJSONParse(sessionStorage.getItem('selectedDataType'), 'sensordepths'),
  );

  const [filterDropdownOptionSelected, setFilterDropdownOptionSelected] = useState(
    safeJSONParse(sessionStorage.getItem('filterDropdownOptionSelected'), 0),
  );
  const [widgetTimeOptionSelected, setWidgetTimeOptionSelected] =
    useState(DEFAULT_WIDGET_TIME_INDEX);
  const [deviceSearchValue, setDeviceSearchValue] = useState('');
  const deferredSelectedDeviceMap = useDeferredValue(selectedDeviceMap);
  const deferredSelectedDataType = useDeferredValue(selectedDataType);

  const selectedDeviceIds = Object.keys(selectedDeviceMap || {}).filter(
    (a) => selectedDeviceMap[a],
  );
  const activeGraphFamily = useMemo(
    () => getSelectedGraphFamily(connectedDevices, selectedDeviceMap),
    [connectedDevices, selectedDeviceMap],
  );
  const deferredGraphFamily = useMemo(
    () => getSelectedGraphFamily(connectedDevices, deferredSelectedDeviceMap),
    [connectedDevices, deferredSelectedDeviceMap],
  );
  const activeFamilyKey = activeGraphFamily?.familyKey ?? null;
  const deferredFamilyKey = deferredGraphFamily?.familyKey ?? null;
  // Map of selected ids restricted to graph-capable devices.
  const selectedGraphDeviceMap = useMemo(() => {
    const map = {};
    graphDeviceIds.forEach((id) => {
      if (selectedDeviceMap?.[id]) {
        map[id] = true;
      }
    });
    return map;
  }, [graphDeviceIds, selectedDeviceMap]);
  const hasSelectedDevice = useMemo(
    () => Object.keys(selectedDeviceMap || {}).some((k) => selectedDeviceMap[k]),
    [selectedDeviceMap],
  );
  const hasDeferredSelectedDevice = useMemo(
    () => Object.keys(deferredSelectedDeviceMap || {}).some((k) => deferredSelectedDeviceMap[k]),
    [deferredSelectedDeviceMap],
  );
  const graphMetricOptions = getGraphMetricOptions(activeGraphFamily, {
    devices: connectedDevices,
    selectedDeviceMap: selectedGraphDeviceMap,
    unitType,
  });
  const filterDropdownOptions = graphMetricOptions.map((option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  ));
  const deviceDropdownOptions = getGraphDeviceOptions(connectedDevices);
  const filteredDeviceDropdownOptions = useMemo(() => {
    const normalizedSearch = deviceSearchValue.trim().toLowerCase();
    if (!normalizedSearch) return deviceDropdownOptions;
    return deviceDropdownOptions.filter((option) => {
      const searchableValue = `${option.label || ''} ${option.value || ''} ${option.group || ''}`;
      return searchableValue.toLowerCase().includes(normalizedSearch);
    });
  }, [deviceDropdownOptions, deviceSearchValue]);
  const dropdownMenuStyle = {
    maxHeight: '18rem',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
  };

  // time fallback (last 24h)
  const [localRange, setLocalRange] = useState(() => {
    const now = Date.now();
    return {
      start: startTime ?? now - 24 * 60 * 60 * 1000,
      end: endTime ?? now,
    };
  });

  useEffect(() => {
    setLocalRange((prev) => ({
      start: typeof startTime === 'number' ? startTime : prev.start,
      end: typeof endTime === 'number' ? endTime : prev.end,
    }));
  }, [startTime, endTime]);

  const resolvedStartTime = widget ? localRange.start : (startTime ?? localRange.start);
  const resolvedEndTime = widget ? localRange.end : (endTime ?? localRange.end);

  // The graph is the ONE place that fetches a SERIES. The page load brings a single
  // newest point per device; everything a chart needs beyond that is asked for
  // here — for the devices the user selected, in the range shown, paging until
  // the backend says there is no more of the window left (the backend caps a
  // request at DEVICE_DATA_MAX_IDS_PER_REQUEST ids and GRAPH_SERIES_PAGE_LIMIT
  // points per device, so a bigger selection goes in chunks and a busy device
  // goes in pages). One page is only the NEWEST slice of the window: asking for
  // page 0 alone drew the last few hours of a 30-day chart across a full-width
  // axis and read as "the gauge stopped reporting" (Mike, 2026-09-04). A device
  // already fetched for this range is not asked again; a range change asks for
  // all of them. The range key is rounded to the minute so a parent that hands
  // down a fresh `Date.now()` on every render cannot start a fetch storm.
  // Before this, a range change fetched EVERY device in the workplace with no
  // ids (Mike, 2026-09-04).
  const [rangeFetching, setRangeFetching] = useState(false);
  // True once a device in this range hit MAX_GRAPH_POINTS_PER_DEVICE — the chart
  // then holds the newest part of the window, and says so.
  const [boundedSeries, setBoundedSeries] = useState(false);
  const fetchedForRangeRef = useRef({ key: null, ids: new Set() });
  // Fetches in flight. The overlay shows while this is above zero, so a fetch
  // that a later selection change overtakes still clears it when it lands.
  const inFlightRef = useRef(0);
  const selectedIdsKey = selectedDeviceIds.join(',');
  const rangeKey = `${Math.floor(resolvedStartTime / 60000)}:${Math.floor(resolvedEndTime / 60000)}`;
  useEffect(() => {
    const ids = selectedIdsKey ? selectedIdsKey.split(',').map(Number).filter(Number.isFinite) : [];
    if (!ids.length || !Number.isFinite(resolvedStartTime) || !Number.isFinite(resolvedEndTime))
      return;
    if (fetchedForRangeRef.current.key !== rangeKey) {
      fetchedForRangeRef.current = { key: rangeKey, ids: new Set() };
      setBoundedSeries(false); // a new window is bounded, or not, on its own merits
    }
    const pending = ids.filter((id) => !fetchedForRangeRef.current.ids.has(id));
    if (!pending.length) return;
    pending.forEach((id) => fetchedForRangeRef.current.ids.add(id));
    inFlightRef.current += 1;
    setRangeFetching(true);
    // The fetched-for-range key IS the request id: it changes when the window
    // changes and NOT when the selection does, so a page that lands for a window
    // the user has already left is dropped, while adding a device never cancels
    // the paging already running for the devices before it.
    const isCurrent = () => fetchedForRangeRef.current.key === rangeKey;
    (async () => {
      try {
        const { capped, superseded } = await fetchGraphSeriesPages({
          fetchRange: fetchNodeDataByTimestamps,
          ids: pending,
          startTime: resolvedStartTime,
          endTime: resolvedEndTime,
          isCurrent,
        });
        if (superseded) return;
        if (capped) setBoundedSeries(true);
      } catch (err) {
        // A failed chunk is asked for again on the next selection or range change
        // — but only while this is still the window those ids were fetched for.
        if (isCurrent()) pending.forEach((id) => fetchedForRangeRef.current.ids.delete(id));
        console.error('Error fetching graph device data:', err);
      } finally {
        inFlightRef.current = Math.max(0, inFlightRef.current - 1);
        setRangeFetching(inFlightRef.current > 0);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdsKey, rangeKey, fetchNodeDataByTimestamps]);
  const defaultGraphMetric = useMemo(
    () =>
      getDefaultGraphMetric(activeGraphFamily, {
        devices: connectedDevices,
        selectedDeviceMap: selectedGraphDeviceMap,
        unitType,
        startTime: resolvedStartTime,
        endTime: resolvedEndTime,
      }),
    [
      activeGraphFamily,
      connectedDevices,
      selectedGraphDeviceMap,
      unitType,
      resolvedStartTime,
      resolvedEndTime,
    ],
  );

  useEffect(() => {
    sessionStorage.setItem(
      'filterDropdownOptionSelected',
      JSON.stringify(filterDropdownOptionSelected),
    );
    sessionStorage.setItem('selectedDeviceMap', JSON.stringify(selectedDeviceMap));
  }, [filterDropdownOptionSelected, selectedDeviceMap]);

  useEffect(() => {
    sessionStorage.setItem('selectedDataType', JSON.stringify(selectedDataType));
  }, [selectedDataType]);

  // Ensure `selectedDeviceMap` contains only IDs that exist in `connectedDevices`.
  useEffect(() => {
    if (!connectedDevices || !connectedDevices.length) return;
    const connectedDeviceIdSet = new Set((connectedDevices || []).map((d) => String(d.id)));
    const storedKeys = Object.keys(selectedDeviceMap || {});
    const cleanedKeys = storedKeys.filter(
      (key) => selectedDeviceMap[key] && connectedDeviceIdSet.has(key),
    );
    if (cleanedKeys.length === storedKeys.length) return; // no change
    const newMap = {};
    cleanedKeys.forEach((key) => (newMap[key] = true));
    setSelectedDeviceMap(newMap);
  }, [connectedDevices]);

  // Track which chart type is active and set an appropriate default dataType only
  // when the mode first changes (null → usgs, null → aware, usgs → null, etc.)
  const prevModeRef = useRef(null);
  useEffect(() => {
    const mode = activeFamilyKey;
    if (mode === prevModeRef.current) return; //Do nothing if the mode hasn't changed

    //Update the ref to the new mode and set the default data type based on the new mode
    prevModeRef.current = mode;
    const newDataType = mode ? defaultGraphMetric : '';
    setSelectedDataType(newDataType);
    // Sync the DropdownSelector index to match the new default so its label updates.
    const newIndex = filterDropdownOptions.findIndex(
      (option) => option && option.props && option.props.value === newDataType,
    );
    setFilterDropdownOptionSelected(newIndex >= 0 ? newIndex : 0);
  }, [activeFamilyKey, filterDropdownOptions, defaultGraphMetric]);

  const fetching = useSelector(selectIntegrationFetching);
  const historyDataLoading = useSelector(selectHistoryDataLoading);
  const initialHistoryLoaded = useSelector(selectInitialHistoryLoaded);
  const reduxSelectedDeviceId = useSelector(selectSelectedDeviceId);
  const linkingGroupId = useSelector(selectLinkingGroupId);
  const { data: allGroups = [] } = useGetGroupsQuery();
  const isChartTransitioning =
    deferredSelectedDeviceMap !== selectedDeviceMap ||
    deferredSelectedDataType !== selectedDataType;

  // Sync graph selection with selectedDeviceID (Only for floodDevices)
  const prevSelectedIdRef = useRef(null);
  const graphDatasheetSelectionRef = useRef(null);
  useEffect(() => {
    if (widget) return; // Disabled in widget mode
    if (reduxSelectedDeviceId == null) {
      // The datasheet closed (Back) or the selection was cleared. The graph's
      // own multi-selection is NOT the same thing as "which device is open":
      // it stays as the user built it. Before this the map was wiped here, so
      // Back on a datasheet emptied the graph (Mike, 2026-08-28).
      prevSelectedIdRef.current = null;
      return;
    }
    const id = String(reduxSelectedDeviceId);
    if (prevSelectedIdRef.current === id) return; // already reflected — skip
    if (graphDatasheetSelectionRef.current === id) {
      graphDatasheetSelectionRef.current = null;
      prevSelectedIdRef.current = id;
      return;
    }
    if (!graphDeviceIds.includes(id)) return; // no graph for this device type — skip
    prevSelectedIdRef.current = id;
    // Viewing a device ADDS it to the graph; it does not replace the devices already graphed.
    setSelectedDeviceMap((prev) => ({ ...(prev || {}), [id]: true }));
  }, [reduxSelectedDeviceId, graphDeviceIds]);

  // Sync graph selection when a group is selected — show all flood-capable devices in the group.
  const prevLinkingGroupIdRef = useRef(null);
  useEffect(() => {
    if (widget) return;
    if (linkingGroupId == null) {
      prevLinkingGroupIdRef.current = null; // reset so re-selecting same group fires fresh
      return;
    }
    if (prevLinkingGroupIdRef.current === linkingGroupId) return;
    prevLinkingGroupIdRef.current = linkingGroupId;
    const newMap = resolveGroupDeviceMap(linkingGroupId, allGroups, graphDeviceIds);
    if (!newMap) return; // group has no graph-capable devices — leave graph as-is
    setSelectedDeviceMap(newMap);
  }, [linkingGroupId, allGroups, graphDeviceIds, widget]);

  // On mount: if Redux has no device selected use sessionStorage
  useEffect(() => {
    if (widget) return; // Disabled in widget mode
    if (reduxSelectedDeviceId != null) return; // Redux already owns the selection
    const stored = safeJSONParse(sessionStorage.getItem('selectedDeviceMap'), null);
    if (!stored) return;
    const activeIds = Object.keys(stored).filter((k) => stored[k]);
    if (activeIds.length === 0) return;
    const firstId = activeIds[0];
    prevSelectedIdRef.current = firstId; // prevent the sync effect from re-triggering
    dispatch(setSelectedDeviceId(Number(firstId) || firstId));
  }, []);

  const handleSelectedDeviceIndex = useCallback(
    (index) => {
      if (typeof onSelectedDeviceIndex !== 'function') return;
      onSelectedDeviceIndex(index);
    },
    [onSelectedDeviceIndex],
  );

  const handleGraphDataTypeChange = useCallback(
    (nextDataType) => {
      setSelectedDataType(nextDataType);
      const nextIndex = filterDropdownOptions.findIndex(
        (option) => option?.props?.value === nextDataType,
      );
      setFilterDropdownOptionSelected(nextIndex >= 0 ? nextIndex : 0);
    },
    [filterDropdownOptions],
  );

  const handleGraphDatasheetSelect = useCallback((deviceId) => {
    graphDatasheetSelectionRef.current = String(deviceId);
  }, []);

  const handleWidgetTimeChange = useCallback(
    (event, item, index) => {
      const selectedIndex = typeof index === 'number' ? index : DEFAULT_WIDGET_TIME_INDEX;
      const nextRange =
        typeof item?.value === 'number'
          ? {
              startTime: Date.now() - item.value * 60 * 1000,
              endTime: Date.now(),
            }
          : getWidgetTimeRangeFromOption(selectedIndex);
      if (!nextRange) return;

      setWidgetTimeOptionSelected(selectedIndex);
      setLocalRange({
        start: nextRange.startTime,
        end: nextRange.endTime,
      });
      setShowCurrentDevice?.(false);
    },
    [setShowCurrentDevice],
  );

  const chartRef = useRef(null);
  const graphControlStyle = constrainControls
    ? { flexBasis: 260, maxWidth: 360 }
    : { flexBasis: 0 };
  const graphTimeControlStyle = constrainControls
    ? { flexBasis: 220, maxWidth: 240 }
    : { flexBasis: 220 };
  const graphToolbarAlignmentClass =
    constrainControls && graphControlsAlignment === 'split'
      ? ' justify-content-md-between'
      : constrainControls
        ? ' justify-content-md-end'
        : '';
  const graphBuildResult = useGraphBuildResult(deferredFamilyKey, {
    title,
    devices: connectedDevices,
    selectedDeviceMap: deferredSelectedDeviceMap,
    selectedDataType: deferredSelectedDataType,
    unitType,
    setUnitType,
    startTime: resolvedStartTime,
    endTime: resolvedEndTime,
    historyDataLoading,
    chartRef,
    dispatch,
    navigate,
    location,
    onSelectedDeviceIndex: handleSelectedDeviceIndex,
    onSelectDataType: handleGraphDataTypeChange,
    onGraphDatasheetSelect: handleGraphDatasheetSelect,
    setShowCurrentDevice,
    setShowProvisional,
  });

  return (
    <Col className="d-flex flex-column h-100 min-h-0">
      <div
        className={`d-flex flex-column flex-md-row gap-2 gap-md-3 align-items-stretch align-items-md-center position-relative z-3${
          graphToolbarAlignmentClass
        }`}
      >
        <div
          className="flex-grow-1 flex-shrink-1 min-w-0 connected-devices-graph-control-responsive"
          style={graphControlStyle}
        >
          <DropdownSelector
            selectedOption={filterDropdownOptionSelected}
            options={filterDropdownOptions}
            setSelected={setFilterDropdownOptionSelected}
            contentStyle={dropdownMenuStyle}
            triggerStyle={{
              minHeight: 38,
              boxShadow: 'none',
              fontSize: 14,
            }}
            onChange={(event, item) => {
              setUnitType(false);
              const nextValue = item?.props?.value ?? item?.value ?? item;
              startTransition(() => {
                setSelectedDataType(nextValue);
              });
            }}
          />
        </div>

        {widget && (
          <div
            className="flex-grow-0 flex-shrink-1 min-w-0 connected-devices-graph-control-responsive"
            style={graphTimeControlStyle}
          >
            <DropdownSelector
              selectedOption={widgetTimeOptionSelected}
              options={WIDGET_TIME_OPTIONS.map((option) => option.label)}
              setSelected={setWidgetTimeOptionSelected}
              contentStyle={dropdownMenuStyle}
              onChange={handleWidgetTimeChange}
            />
          </div>
        )}

        <div
          className="flex-grow-1 flex-shrink-1 min-w-0 connected-devices-graph-control-responsive"
          style={graphControlStyle}
        >
          <BaseSearchDropdown
            className="w-100 min-w-0"
            searchValue={deviceSearchValue}
            onSearchValueChange={setDeviceSearchValue}
            searchPlaceholder="Search devices"
            searchAriaLabel="Search graph devices"
            dropdownContainerClassName="w-100"
            searchDropdownContent={
              <DropdownSelect
                multiple
                menuOnly
                selectedOption={selectedDeviceIds}
                options={filteredDeviceDropdownOptions}
                setSelected={(arr) => {
                  const map = {};
                  (Array.isArray(arr) ? arr : []).forEach((id) => {
                    map[String(id)] = true;
                  });
                  startTransition(() => {
                    setSelectedDeviceMap(map);
                  });
                }}
                contentStyle={dropdownMenuStyle}
                emptyMessage="No devices found."
                onClearAll={() => {
                  startTransition(() => {
                    setSelectedDeviceMap({});
                    setSelectedDataType('');
                  });
                  if (!widget) {
                    dispatch(setSelectedDeviceId(null));
                    prevSelectedIdRef.current = null;
                  }
                }}
                onChange={(event, item) => {
                  const id = String(item);
                  const turningOn = !selectedDeviceMap[id];
                  startTransition(() => {
                    setSelectedDeviceMap((prev) => ({
                      ...prev,
                      [id]: !prev[id],
                    }));
                  });
                  if (turningOn && !widget) {
                    prevSelectedIdRef.current = id;
                    dispatch(setSelectedDeviceId(Number(id) || id));
                  }
                }}
                isOptionDisabled={(option) => {
                  return isGraphOptionDisabled(activeFamilyKey, option);
                }}
              />
            }
            dropdownContent={
              <DropdownSelect
                multiple
                selectedOption={selectedDeviceIds}
                options={deviceDropdownOptions}
                setSelected={(arr) => {
                  const map = {};
                  (Array.isArray(arr) ? arr : []).forEach((id) => {
                    map[String(id)] = true;
                  });
                  startTransition(() => {
                    setSelectedDeviceMap(map);
                  });
                }}
                contentStyle={dropdownMenuStyle}
                onClearAll={() => {
                  startTransition(() => {
                    setSelectedDeviceMap({});
                    setSelectedDataType('');
                  });
                  if (!widget) {
                    dispatch(setSelectedDeviceId(null));
                    prevSelectedIdRef.current = null;
                  }
                }}
                onChange={(event, item) => {
                  const id = String(item);
                  const turningOn = !selectedDeviceMap[id];
                  startTransition(() => {
                    setSelectedDeviceMap((prev) => ({
                      ...prev,
                      [id]: !prev[id],
                    }));
                  });
                  if (turningOn && !widget) {
                    prevSelectedIdRef.current = id;
                    dispatch(setSelectedDeviceId(Number(id) || id));
                  }
                }}
                isOptionDisabled={(option) => {
                  return isGraphOptionDisabled(activeFamilyKey, option);
                }}
              />
            }
          />
        </div>
        {expandButton && (
          <div className="d-flex align-items-stretch justify-content-end flex-shrink-0">
            {expandButton}
          </div>
        )}
      </div>
      <div className="pt-2 pb-0 flex-fill w-100 min-h-0 device-graph-scroll-container position-relative">
        {/* Show loading overlay while initial historical data is being fetched */}
        {fetching && !initialHistoryLoaded && hasSelectedDevice && (
          <div className="position-absolute top-0 end-0 m-2 px-2 py-1 rounded bg-dark bg-opacity-75 text-white z-1 small pe-none">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading historical data...
            </div>
          </div>
        )}
        {(isChartTransitioning || rangeFetching || (historyDataLoading && hasSelectedDevice)) && (
          <div className="position-absolute top-0 end-0 m-2 px-2 py-1 rounded bg-dark bg-opacity-75 text-white z-2 small pe-none">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading chart data...
            </div>
          </div>
        )}
        {/* The x-axis is pinned to the whole window, so a bounded series would
            otherwise read as a gauge that stopped reporting. `mt-5` clears the
            fetching chip above, which can still be up for another device. */}
        {boundedSeries && (
          <div
            className="position-absolute top-0 end-0 mt-5 me-2 px-2 py-1 rounded bg-dark bg-opacity-75 text-white z-2 small pe-none"
            role="status"
          >
            Showing the newest {MAX_GRAPH_POINTS_PER_DEVICE.toLocaleString()} points of this range
            for each device
          </div>
        )}
        {connectedDevicesLoading && (
          <div className="position-absolute top-0 start-0 m-2 px-2 py-1 rounded bg-dark bg-opacity-75 text-white z-1 small pe-none">
            <ConnectedDevicesLoadingState message="Loading graph devices..." />
          </div>
        )}
        {hasDeferredSelectedDevice ? (
          <GraphRenderer
            result={graphBuildResult}
            chartRef={chartRef}
            startTime={resolvedStartTime}
            endTime={resolvedEndTime}
            onViewDevice={setShowCurrentDevice ? () => setShowCurrentDevice(true) : undefined}
          />
        ) : (
          <div className="bg-primary text-white d-flex align-items-center justify-content-center rounded-3 h-100 w-100">
            <div className="fw-semibold">Select a device</div>
          </div>
        )}
      </div>
    </Col>
  );
};

export default DeviceGraph;
